import type { ImageEvaluation } from "@/lib/api/agents"
import type { PhotoVerdict, ShotKey } from "./types"
import { SHOT_SEQUENCE } from "./types"

// Turn an agent ImageEvaluation into the user-facing verdict the
// photo screen renders. The agent already classifies each photo
// (qualified bool, qualityScore, qualityIssues array, side); we
// just map that to one actionable instruction.
//
// Logic order matters:
//   1. Side mismatch is the FIRST thing to surface — if the user
//      pointed the camera the wrong way, no other quality check
//      matters. They need to know they're on the wrong angle.
//   2. Then qualityIssues — we look at the strings to pick a
//      specific verdict (close / dark / blurry).
//   3. Then qualityScore as a coarse fallback for any other
//      "low quality but I can't tell why" case.
//   4. Else "good".

const CLOSE_KEYWORDS = ["close", "framing", "stand back", "too near"]
const DARK_KEYWORDS = ["dark", "lighting", "underexpos", "shadow"]
const BLUR_KEYWORDS = ["blur", "shake", "out of focus", "motion"]

export interface VerdictArgs {
  evaluation: Pick<ImageEvaluation, "qualified" | "qualityScore" | "qualityIssues" | "side">
  expectedShot: ShotKey
}

export function deriveVerdict({ evaluation, expectedShot }: VerdictArgs): PhotoVerdict {
  const expected = SHOT_SEQUENCE.find((s) => s.key === expectedShot)?.side
  if (expected && !sideMatches(evaluation.side, expected)) {
    return "wrong-side"
  }

  const issuesText = (evaluation.qualityIssues ?? []).join(" ").toLowerCase()
  if (matches(issuesText, CLOSE_KEYWORDS)) return "too-close"
  if (matches(issuesText, DARK_KEYWORDS))  return "too-dark"
  if (matches(issuesText, BLUR_KEYWORDS))  return "blurry"

  if (!evaluation.qualified || evaluation.qualityScore < 60) {
    return "low-quality"
  }

  return "good"
}

function matches(haystack: string, needles: string[]): boolean {
  return needles.some((n) => haystack.includes(n))
}

// "Side matches expected" with a tolerance band — the agent might
// say FRONT_LEFT_CORNER when the user shot from a slightly angled
// front. We accept any front-ish detection for the front slot,
// any side-ish for left/right slots, etc.
function sideMatches(detected: string, expected: string): boolean {
  // Exact hit.
  if (detected === expected) return true
  // Front family.
  if (expected === "FRONT_CENTER") {
    return /^FRONT/.test(detected) && !/_SIDE$/.test(detected)
  }
  // Rear family.
  if (expected === "REAR_CENTER") {
    return /^REAR/.test(detected) && !/_SIDE$/.test(detected)
  }
  // Left side family — covers LEFT_SIDE, FRONT_LEFT_SIDE, REAR_LEFT_SIDE.
  if (expected === "LEFT_SIDE") {
    return /LEFT_SIDE$/.test(detected)
  }
  // Right side family — covers RIGHT_SIDE, FRONT_RIGHT_SIDE, REAR_RIGHT_SIDE.
  if (expected === "RIGHT_SIDE") {
    return /RIGHT_SIDE$/.test(detected)
  }
  return false
}
